import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_PERCENTUAL,
  CASAS_QUANTIDADE,
  compararDinheiro,
  desescalar,
  dividirArredondando,
  escalar,
  multiplicarQuantidadePorValor,
  somarDinheiro,
  somarQuantidade,
  subtrairDinheiro,
} from './aritmetica.ts';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  percentualSchema,
  precoUnitarioSchema,
  quantidadeSchema,
  regimeTributarioSchema,
  respostaListaSchema,
  situacaoNotaSchema,
  tipoInsumoSchema,
  uuidSchema,
} from './comum.ts';
// A mesma escala das duas metades do fator no cadastro do fornecedor. Aqui ele
// chega como decimal — é registro do que aconteceu, e não a fonte da conta.
import { fatorConversaoSchema } from './conversoes-unidade-medida.ts';
import { freteCotacaoSchema, freteTemValor, type FreteCotacao } from './cotacoes-preco.ts';
import {
  impostosDestacadosEntradaSchema,
  impostosDestacadosSchema,
  impostosZerados,
  type ImpostosDestacadosEntrada,
} from './impostos.ts';

/**
 * Nota de compra (`.spec/010`, "Nota de Compra"; `.spec/016`, seção 8).
 *
 * É o documento **fiscal** da compra: o que o fornecedor entregou e faturou. Vem
 * depois do pedido e é ele quem, ao ser confirmado, movimenta estoque, forma o
 * custo médio e vira contas a pagar.
 *
 * Quatro coisas a distinguem do pedido, que serviu de molde:
 *
 * - **Duas situações, e não quatro.** `PENDENTE` e `CONFIRMADA` (`.spec/010`).
 *   Não há "cancelada": a nota que não deveria existir se **exclui**, revertendo
 *   os efeitos, e é por isso que a exclusão tem regra própria (`.spec/010`,
 *   "Exclusão de Nota de Compra confirmada"). Uma situação de cancelamento
 *   deixaria no sistema um documento fiscal que não existe no mundo.
 * - **O crédito de imposto é apurado aqui.** O pedido não conhece a operação
 *   fiscal do item nem aplica o regime da empresa; a nota conhece as duas coisas
 *   e por isso é ela quem compõe o custo do estoque de verdade. Os créditos são
 *   **recalculados a cada gravação enquanto pendente e congelados na
 *   confirmação** (`.spec/010`). Os impostos destacados, esses, são **os mesmos
 *   do pedido e da cotação** desde que a nota adotou o desenho comum de
 *   `impostos.ts` — ver `impostosDoItemDeNotaEntradaSchema`.
 * - **O rateio é por quantidade e depósito**, como no pedido — e isto **reverte**
 *   o que estava escrito aqui: "o rateio é por percentual, porque o que se divide
 *   é dinheiro". A premissa era que o destino físico já estava no item, uma vez
 *   só. Não está: o pedido separa dez sacos para o canteiro da Aurora e cinco
 *   para o almoxarifado, e a nota que dava entrada tudo num depósito só punha
 *   quinze sacos onde havia dez — o saldo do canteiro passava a mentir para quem
 *   requisita lá, e ninguém concilia isso depois.
 *
 *   O índice percentual **continua existindo**, e é ele que o contas a pagar
 *   herda (`.spec/010`, "Geração de Contas a Pagar"). O que mudou é quem o
 *   escreve: passou a ser **derivado da quantidade** e gravado. Não entra no
 *   payload, pela mesma razão que `valorTotal` não entra — é conta sobre o que
 *   veio, e aceitá-lo deixaria o cliente gravar um dinheiro que não corresponde
 *   ao material que ele mesmo mandou.
 * - **Tem financeiro.** As parcelas do que se deve ao fornecedor viajam no mesmo
 *   payload, e a soma delas tem de bater com o valor do documento — ver
 *   `valorAPagarDaNota`.
 *
 * O que este arquivo **não** cobre é a confirmação e seus efeitos (movimentação
 * de estoque, custo médio, consumo automático, contas a pagar) nem a exclusão que
 * os reverte. Eles são atos sobre outras cinco tabelas e vivem fora daqui.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e rótulos                                                          */
/* -------------------------------------------------------------------------- */

export const SITUACOES_NOTA_COMPRA = situacaoNotaSchema.options;
export type SituacaoNotaCompra = z.infer<typeof situacaoNotaSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_SITUACAO_NOTA: Readonly<Record<SituacaoNotaCompra, string>> = {
  PENDENTE: 'Pendente',
  CONFIRMADA: 'Confirmada',
};

/**
 * O que cada situação significa, para a dica da tela.
 *
 * A diferença entre as duas não é de estágio de digitação: é o instante em que a
 * nota passa a ter efeito sobre estoque e financeiro. Escrever "em digitação" e
 * "finalizada" esconderia isso.
 */
export const DICA_SITUACAO_NOTA: Readonly<Record<SituacaoNotaCompra, string>> = {
  PENDENTE:
    'Digitada e sem efeito nenhum: não há entrada no estoque nem título a pagar. É a única situação que se altera.',
  CONFIRMADA:
    'Já movimentou o estoque e gerou o financeiro. Os créditos de imposto ficaram congelados como estavam no dia.',
};

/**
 * Quem paga o frete — os **mesmos três códigos** da cotação e do pedido.
 *
 * O schema é reaproveitado em vez de redeclarado: o frete que o pedido fechou é
 * o frete que a nota fatura, e três enums com os mesmos valores acabariam
 * divergindo no dia em que alguém acrescentasse um quarto num só lugar.
 *
 * A **modalidade** mora no cabeçalho, como no pedido: a nota é de um fornecedor
 * só, e "quem entrega" não muda de linha para linha. O **valor** desceu para o
 * item — ver `valorFrete` em `itemNotaCompraEntradaSchema`.
 */
export const freteNotaSchema = freteCotacaoSchema;
export type FreteNota = FreteCotacao;

/**
 * Teto de itens por nota.
 *
 * O mesmo do pedido, e pela mesma razão: existe para que um `curl` com dez mil
 * linhas não vire dez mil `INSERT` numa transação, e não para julgar o negócio.
 * Uma NF-e cabe em até 990 itens, mas a nota de material de obra com mais de
 * duzentas linhas é caso de conferência manual de qualquer forma.
 */
export const MAXIMO_ITENS_NOTA = 200;

/** Teto de rateios por item — o mesmo do pedido. */
export const MAXIMO_RATEIOS_ITEM_NOTA = 20;

/**
 * Teto de parcelas do financeiro da nota.
 *
 * O mesmo teto do cadastro de condição de pagamento (`MAXIMO_PARCELAS_CONDICAO`),
 * porque é dela que as parcelas costumam sair. Um número menor aqui recusaria uma
 * condição legítima já cadastrada, e o usuário leria isso como defeito da nota.
 */
export const MAXIMO_PARCELAS_NOTA = 120;

/**
 * Chave de acesso da NF-e: 44 dígitos.
 *
 * A validação para no formato de propósito. O 44º dígito é verificador (módulo
 * 11) e conferi-lo seria possível — mas a chave também é digitada de nota de
 * fornecedor pequeno emitida em regime que não usa NF-e, e recusar por dígito
 * travaria o lançamento de quem está com o documento na mão. O que o `regex`
 * pega é o erro comum: 43 ou 45 dígitos, colados de um leitor.
 */
export const chaveNfeSchema = z
  .string()
  .trim()
  .regex(/^\d{44}$/, 'A chave da NF-e tem exatamente 44 dígitos.');

/**
 * O número impresso no documento do fornecedor.
 *
 * **Obrigatório**, ao contrário da chave: a chave é o arquivo eletrônico, que
 * pode não existir ou não ter chegado, e o número está em todo documento que o
 * financeiro vai pagar — inclusive no recibo do fornecedor pequeno.
 *
 * Texto, e não inteiro, ainda que a NF-e o traga numérico: "000431" digitado do
 * papel vira 431 num campo numérico, e a conferência com o boleto passa a
 * comparar coisas escritas de formas diferentes. Sem `regex` pelo mesmo motivo —
 * a nota de serviço municipal traz número com barra e com letra.
 *
 * Vinte caracteres é folga de uma ordem de grandeza sobre os nove dígitos da
 * NF-e, e existe só para impedir que um campo de texto livre receba um parágrafo.
 */
export const numeroNotaSchema = z
  .string()
  .trim()
  .min(1, 'Informe o número da nota.')
  .max(20, 'O número da nota tem no máximo 20 caracteres.');

/**
 * A série do documento — `1`, `U`, `001`.
 *
 * Obrigatória por decisão do projeto: o par número × série é o que identifica a
 * nota no diálogo com o fornecedor e no arquivo fiscal, e uma série em branco
 * obrigaria quem confere a adivinhar se ela não existe ou se ninguém digitou.
 * Quem lida com documento sem série declarada usa `1`, que é o que a legislação
 * chama de série única.
 */
export const serieNotaSchema = z
  .string()
  .trim()
  .min(1, 'Informe a série da nota.')
  .max(10, 'A série tem no máximo 10 caracteres.');

/* -------------------------------------------------------------------------- */
/* Impostos do item                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A tributação do item — **o mesmo desenho do pedido e da cotação**
 * (`impostos.ts`).
 *
 * Eram doze valores soltos, sem base nem alíquota, e o conferente copiava do
 * papel do fornecedor sem que houvesse como refazer a conta. A nota era o último
 * dos quatro documentos de compra fora do desenho comum; este alias é o fim
 * disso, e era o que o comentário de `impostos.ts` já prometia ao dizer "o
 * pedido e a nota recebem o mesmo desenho depois".
 *
 * O que a mudança compra não é uniformidade, são três coisas concretas:
 *
 * - **A entrada por pedido pode copiar os impostos.** Enquanto os formatos
 *   divergiam, o rascunho nascia com tudo em branco porque não havia tradução
 *   possível — o pedido tinha base e alíquota e a nota só o valor.
 * - **A conferência sobrevive à reabertura.** Base e alíquota gravadas dizem
 *   *por que* o imposto é aquele; o valor sozinho só diz quanto.
 * - **Os campos são os do tipo do insumo da linha.** Perguntar ISS num item de
 *   cimento era oferecer um campo que nunca se preenche. Quem decide é
 *   `blocosDeImposto`, sobre o `insumoTipo` que a leitura devolve.
 *
 * **Três impostos saíram**, e as três saídas têm motivo:
 *
 * - **PIS e COFINS por dentro.** É deles que saem `valorCreditoPis` e
 *   `valorCreditoCofins`, e por isso os créditos passam a apurar **zero** até
 *   que o desenho comum ganhe o bloco deles. As colunas do banco e os campos de
 *   leitura continuam onde estão: nota já confirmada guarda o crédito que
 *   apurou, e congelado é o que ele tem de ser.
 * - **`valorRetencoes` somado** virou os três retidos separados — PIS, COFINS e
 *   CSLL —, que é o que diz o que recolher em cada guia.
 * - **O Imposto Seletivo** depende da reforma tributária e ainda não está em
 *   vigor. Volta quando estiver, e no bloco comum, junto com IBS e CBS.
 */
export const impostosDoItemDeNotaEntradaSchema = impostosDestacadosEntradaSchema;
export type ImpostosDoItemDeNotaEntrada = ImpostosDestacadosEntrada;

/**
 * Os impostos como a API os devolve — os destacados **mais os créditos
 * apurados**.
 *
 * É a única diferença em relação ao pedido, e ela é da nota: os créditos vêm na
 * leitura porque é o que explica o custo — sem eles, um item de 1.000,00 que
 * entrou no estoque por 820,00 pareceria erro de cálculo.
 *
 * Os três continuam **fora do payload de entrada**, e a ausência é o ponto: eles
 * dependem da operação fiscal do item e do regime tributário da empresa da nota
 * (`.spec/010`, "Composição do custo do estoque"), e aceitá-los digitados
 * permitiria creditar ICMS numa compra de empresa do Simples — que é exatamente
 * o erro que subavalia o custo em silêncio.
 */
export const impostosDoItemDeNotaSchema = impostosDestacadosSchema.extend({
  /** Apurado pelo servidor. Zero quando a operação não credita ou a empresa não é `NORMAL`. */
  valorCreditoIcms: dinheiroSchema,
  /** Zerado enquanto o PIS por dentro não tiver bloco no desenho comum. */
  valorCreditoPis: dinheiroSchema,
  /** Zerado enquanto a COFINS por dentro não tiver bloco no desenho comum. */
  valorCreditoCofins: dinheiroSchema,
});

export type ImpostosDoItemDeNota = z.infer<typeof impostosDoItemDeNotaSchema>;

/** Os impostos destacados, todos zerados — o mesmo conjunto do pedido. */
export function impostosDeNotaZerados(): ImpostosDoItemDeNotaEntrada {
  return impostosZerados();
}

/* -------------------------------------------------------------------------- */
/* Crédito e custo de estoque                                                 */
/* -------------------------------------------------------------------------- */

/** As três flags da operação fiscal **do item** que decidem o crédito. */
export interface CreditosDaOperacaoFiscal {
  creditaIcms: boolean;
  creditaPis: boolean;
  creditaCofins: boolean;
}

/**
 * Só empresa de regime `NORMAL` credita imposto (`.spec/015`, item 3).
 *
 * A empresa avaliada é a **da nota** — quem compra —, e não o fornecedor: o
 * regime do fornecedor decide o que ele destaca no documento; o direito ao
 * crédito é de quem adquire (`.spec/010`).
 *
 * A regra vive no cálculo, e não como validação no cadastro da operação fiscal,
 * porque a operação é do tenant e pode ser usada por empresas de regimes
 * diferentes. Bloqueá-la no cadastro impediria a empresa `NORMAL` de usá-la.
 *
 * `null` é a empresa cujo regime a tela ainda não carregou: não credita. Falhar
 * fechado é o certo — creditar por otimismo mostraria um custo menor que o
 * gravado, e a correção apareceria só ao salvar.
 */
export function podeCreditar(regime: z.infer<typeof regimeTributarioSchema> | null): boolean {
  return regime === 'NORMAL';
}

/** O que o item custa, depois das contas. Strings decimais, como tudo. */
export interface ValoresDoItemDeNota {
  /** `quantidade × valorUnitario`, arredondado a centavo. */
  valorInsumo: string;
  /**
   * `valorInsumo + valorOutros + valorFrete − valorDesconto`. A mercadoria
   * líquida desta linha, com o transporte dela dentro.
   *
   * **O frete entra aqui**, e a mudança veio das duas pontas da cadeia. Antes ele
   * era um valor único no cabeçalho da nota, somado ao valor do documento e a
   * item nenhum — o que significava que ele não entrava no custo de estoque de
   * material nenhum, e quem quisesse o transporte no custo o digitava em "outros"
   * da linha.
   *
   * - **Atrás**, o pedido já cobra frete por linha, e a entrada por pedido tinha
   *   de somar o frete de vários pedidos num campo só do cabeçalho — perdendo de
   *   qual material era cada parte. Aquele código carregava o comentário dizendo
   *   que era o último ponto da cadeia em que isso acontecia.
   * - **À frente**, o custo de cada material passa a sair pronto da linha. O
   *   frete do carregamento continua sendo informado de uma vez: quem o reparte é
   *   o botão de distribuir totais da tela, na proporção da mercadoria, e o
   *   resultado fica visível e corrigível linha a linha.
   */
  valorTotal: string;
  /**
   * O que entra no estoque.
   *
   * `valorTotal + IPI + ICMS ST − créditos de ICMS/PIS/COFINS` (`.spec/010`).
   *
   * O IPI e o ICMS ST somam porque são cobrados **por fora** — o fornecedor os
   * acrescenta ao que cobra. ICMS, PIS e COFINS são cobrados **por dentro**, já
   * estão embutidos no preço, e por isso só afetam o custo quando há crédito, e
   * nesse caso subtraem.
   *
   * O erro fácil é somar ICMS, PIS ou COFINS por simetria com o IPI quando a
   * flag é falsa: isso inflaria o custo pelo próprio valor do imposto — 18% no
   * caso do ICMS — e apareceria meses depois como margem de obra estranha.
   *
   * O piso em zero é **rede, não regra** — e a linha aqui dizia o contrário.
   *
   * Ela afirmava que "crédito maior que a mercadoria é impossível", e não era: um
   * ICMS destacado de R$ 2.000,00 sobre R$ 1.000,00 de mercadoria era aceito sem
   * recusa nenhuma, o custo dava negativo, o piso gravava R$ 0,00 e o material
   * entrava no estoque valendo nada. Dez unidades a R$ 100,00 viravam vinte a
   * R$ 50,00, e todo consumo de obra dali em diante saía por metade — sem nada na
   * tela acusar, porque zero é um número plausível.
   *
   * Quem recusa agora é `validarImpostoPorDentroDosItens`, no payload: o imposto
   * cobrado por dentro não pode passar do que se deve pela linha. O piso continua
   * aqui porque esta função é chamada enquanto o conferente digita, sobre valores
   * que ainda não passaram por validação nenhuma — chegar a ele numa gravação é
   * sinal de caminho que escapou do schema, não de nota estranha.
   */
  valorCustoEstoque: string;
  /** Apurados e gravados, para o levantamento de créditos por período (`.spec/010`). */
  valorCreditoIcms: string;
  valorCreditoPis: string;
  valorCreditoCofins: string;
  /**
   * O que se deve ao fornecedor por esta linha: `valorTotal + IPI + ICMS ST`.
   *
   * Difere do custo de estoque exatamente pelos créditos — o crédito reduz o
   * custo do material, não o que se paga. Confundir os dois é o que faria as
   * parcelas do financeiro somarem menos que a nota.
   */
  valorAPagar: string;
}

/**
 * Os valores derivados do item, calculados **aqui**.
 *
 * Vivem no contrato, e não no service, pelo mesmo motivo de
 * `valoresDoItemDePedido`: a tela mostra o custo enquanto o conferente digita e o
 * servidor grava o número que vale. Duas implementações discordariam em algum
 * arredondamento, e a divergência apareceria como custo que muda ao salvar.
 *
 * As contas são sobre inteiros escalados com `BigInt`, e não com `decimal.js` —
 * ela é dependência de `@construtora/domain`, que o front não carrega, e
 * trazê-la para o contrato colocaria a biblioteca no bundle de toda tela do
 * sistema.
 *
 * Esta função **substituiu** `calcularCustoEstoque`, que vivia em
 * `@construtora/domain/custo.ts` e nunca chegou a ser chamada por ninguém. Manter
 * as duas seria a duplicação que o projeto evita em todo lugar: a mesma regra em
 * dois pacotes diverge na primeira correção, e a divergência apareceria como
 * custo que muda entre a tela e o gravado. A regra do regime (`podeCreditar`) e
 * os testes de crédito vieram junto.
 */
export function valoresDoItemDeNota(
  item: {
    quantidade: string;
    valorUnitario: string;
    valorOutros: string;
    /** Só existe no frete `CIF` do cabeçalho. Zero nas outras modalidades. */
    valorFrete?: string | undefined;
    valorDesconto: string;
    impostos: {
      valorIcms: string;
      valorIcmsSt: string;
      valorIpi: string;
    };
  },
  contexto: {
    /** As flags da operação fiscal **do item**, que prevalece sobre a da nota. */
    operacao: CreditosDaOperacaoFiscal;
    /** Regime da empresa da nota. `null` enquanto a tela não o conhece. */
    regimeEmpresa: z.infer<typeof regimeTributarioSchema> | null;
  },
): ValoresDoItemDeNota {
  const insumo = multiplicarQuantidadePorValor(item.quantidade, item.valorUnitario);

  const total =
    insumo +
    escalar(item.valorOutros, CASAS_DINHEIRO) +
    escalar(item.valorFrete ?? '0', CASAS_DINHEIRO) -
    escalar(item.valorDesconto, CASAS_DINHEIRO);

  const credita = podeCreditar(contexto.regimeEmpresa);
  const creditoDe = (valor: string, flag: boolean): bigint =>
    credita && flag ? escalar(valor, CASAS_DINHEIRO) : 0n;

  const creditoIcms = creditoDe(item.impostos.valorIcms, contexto.operacao.creditaIcms);

  /*
    PIS e COFINS por dentro **não têm mais de onde sair**, e por isso o crédito
    dos dois é zero.

    Os campos deles saíram quando a nota adotou o desenho comum de impostos —
    ver `impostosDoItemDeNotaEntradaSchema`. As flags `creditaPis` e
    `creditaCofins` da operação fiscal continuam existindo e continuam sendo
    lidas, porque é do par flag × valor que o crédito nasce: quando o bloco de
    PIS e COFINS por dentro entrar no desenho comum, é só a metade que falta que
    volta, e as três linhas abaixo voltam a ser simétricas.

    Zerar aqui, e não remover as flags, também é o que mantém honesto o
    congelamento: a nota confirmada guarda o crédito que apurou no dia, e
    `valorCreditoPis` continua sendo coluna e campo de leitura.
  */
  const creditoPis = 0n;
  const creditoCofins = 0n;

  const porFora =
    escalar(item.impostos.valorIpi, CASAS_DINHEIRO) +
    escalar(item.impostos.valorIcmsSt, CASAS_DINHEIRO);

  const aPagar = total + porFora;
  const custo = aPagar - creditoIcms - creditoPis - creditoCofins;

  return {
    valorInsumo: desescalar(insumo, CASAS_DINHEIRO),
    valorTotal: desescalar(total, CASAS_DINHEIRO),
    valorCustoEstoque: desescalar(custo < 0n ? 0n : custo, CASAS_DINHEIRO),
    valorCreditoIcms: desescalar(creditoIcms, CASAS_DINHEIRO),
    valorCreditoPis: desescalar(creditoPis, CASAS_DINHEIRO),
    valorCreditoCofins: desescalar(creditoCofins, CASAS_DINHEIRO),
    valorAPagar: desescalar(aPagar, CASAS_DINHEIRO),
  };
}

/* -------------------------------------------------------------------------- */
/* Rateio de obra                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Uma fatia do item: quanto vai para qual obra, e por qual depósito entra
 * (`.spec/010`, "Rateio Obra").
 *
 * **Por quantidade e com depósito, como no pedido.** É a reversão descrita no
 * topo do arquivo, e ela tem duas consequências que valem repetir aqui:
 *
 * 1. **A entrada de estoque passou a ser por fatia.** Antes o item inteiro entrava
 *    no depósito do item; agora cada fatia entra no depósito dela. É o motivo da
 *    mudança: o pedido separa o material por canteiro, e a nota que o unificava
 *    criava saldo onde não havia material.
 * 2. **O índice de dinheiro é derivado, não digitado.** Ele não está neste
 *    schema: `indicesDoRateioPorQuantidade` o calcula na gravação e a API o grava.
 *    Digitar os dois permitiria 40% do dinheiro numa obra que recebeu 60% do
 *    material, que é a divergência que esta mudança existe para fechar.
 *
 * A obra é **obrigatória**, e aqui fica a diferença que sobra em relação ao
 * pedido: lá a fatia sem obra é a parcela de almoxarifado central. Aqui a lista
 * inteira pode ser vazia para dizer isso, e uma fatia sem obra dentro de um
 * rateio que cobre a linha toda seria custo apontado para lugar nenhum — o rateio
 * do título não saberia em que obra pô-lo.
 */
export const rateioDoItemDeNotaEntradaSchema = z.object({
  obraId: uuidSchema,
  /** Etapa da obra. Opcional; a API recusa a etapa de outra obra. */
  obraParteId: uuidSchema.nullable().prefault(null),
  /**
   * Para onde **esta parte** do material vai.
   *
   * Obrigatório mesmo no item de serviço, em que nada entra em depósito nenhum:
   * a coluna é `NOT NULL`, e a tela grava o depósito do item. É a mesma escolha
   * do rateio do pedido, e pela mesma razão — um `null` aqui obrigaria toda
   * leitura de estoque a tratar o caso que nunca acontece.
   */
  depositoId: uuidSchema,
  /**
   * Quanto desta linha vai para esta obra.
   *
   * A soma das fatias é **exatamente** a quantidade do item — ou o rateio não
   * existe. Quem cobra é `rateioFechaNaQuantidade`, e é essa regra que garante
   * que tudo o que a nota diz ter entrado tenha entrado em algum depósito.
   */
  quantidade: quantidadeSchema.refine(
    (v) => Number(v) > 0,
    'A quantidade da fatia deve ser maior que zero.',
  ),
});

export type RateioDoItemDeNotaEntrada = z.infer<typeof rateioDoItemDeNotaEntradaSchema>;

/** Rateio como a API o devolve, com os nomes resolvidos. */
export const rateioDoItemDeNotaSchema = z.object({
  id: uuidSchema,
  obraId: uuidSchema,
  obraNome: z.string().nullable(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  quantidade: quantidadeSchema,
  /**
   * O índice derivado, como foi **gravado**.
   *
   * Vem na resposta ainda que a tela não o peça: é ele que o contas a pagar
   * herdou, e recalculá-lo para exibir faria a nota antiga mostrar um número
   * diferente do que gerou o título dela.
   */
  indiceRateio: percentualSchema,
});

export type RateioDoItemDeNota = z.infer<typeof rateioDoItemDeNotaSchema>;

/**
 * O rateio de um item **cabe** na linha: as fatias não somam mais do que ela tem.
 *
 * **Não exige cobrir a linha inteira**, e isto reverte a regra que valeu por
 * algumas horas ("a soma é exatamente a quantidade do item"). O que a derrubou é
 * o papel do rateio: quem manda é a nota, e o rateio do pedido é sugestão. O
 * pedido que separa dez sacos para a obra e cinco para o almoxarifado tem cinco
 * sem obra — e, com a soma exata, a sugestão inteira era descartada para não
 * chegar um rascunho inválido, inclusive a parte que o pedido sabia responder.
 *
 * O que **não** for rateado entra no depósito do item, que é o mesmo que a linha
 * sem rateio nenhum sempre fez. A lista vazia continua legítima e continua sendo
 * o caso comum.
 *
 * Quem cobra a obra é outra regra, e é dos cadastros: a conta contábil que exige
 * obra, com a operação fiscal que não dispensa, recusa a linha **sem fatia
 * nenhuma** (`conta_contabil_exige_obra`). A soma não é o lugar dessa cobrança.
 *
 * A comparação é sobre inteiros escalados porque três fatias de 33,3333 e uma de
 * 0,0001 empatam em ponto flutuante e passariam.
 */
export function rateioCabeNaQuantidade(
  quantidade: string,
  rateios: readonly { quantidade: string }[],
): boolean {
  if (rateios.length === 0) return true;

  return (
    rateios.reduce((soma, rateio) => soma + escalar(rateio.quantidade, CASAS_QUANTIDADE), 0n) <=
    escalar(quantidade, CASAS_QUANTIDADE)
  );
}

/**
 * O índice de dinheiro de cada fatia, derivado da quantidade dela.
 *
 * É o que a API grava em `indice_rateio`, e o que o contas a pagar lê depois. A
 * conta é a fração que a fatia representa do material **da linha** — e não do
 * material rateado.
 *
 * **A soma pode ser menor que 100%**, e é o ponto: o rateio que cobre dez dos
 * quinze sacos da linha responde por dois terços do custo dela, e o terço
 * restante fica sem obra — que é exatamente o que a linha sem rateio nenhum já
 * fazia. O rateio do título soma os índices de todos os itens e já convive com
 * total abaixo de cem; escalar até fechar atribuiria às obras um custo que não
 * era delas.
 *
 * **Toda fatia recebe ao menos 0,0001%**, que é o mínimo representável e o que o
 * `CHECK` do banco exige. A fatia minúscula existe: cinco mil litros para uma
 * obra e um litro para outra dão 0,02% — que cabe —, mas um décimo de litro em
 * dez mil não caberia, e gravar zero faria a gravação inteira falhar num `CHECK`
 * que não explica nada. O centésimo de milésimo emprestado sai da maior fatia,
 * onde não muda o dinheiro em centavo nenhum.
 *
 * A sobra do arredondamento vai para as fatias de **maior resto**, com empate
 * pela ordem — a mesma mecânica de `ratear`, em `@construtora/domain`. O
 * resultado é determinístico: gravar a mesma nota duas vezes dá o mesmo índice.
 */
export function indicesDoRateioPorQuantidade(
  quantidadeDoItem: string,
  rateios: readonly { quantidade: string }[],
): string[] {
  if (rateios.length === 0) return [];

  const total = escalar(quantidadeDoItem, CASAS_QUANTIDADE);
  const escaladas = rateios.map((rateio) => escalar(rateio.quantidade, CASAS_QUANTIDADE));
  const rateada = escaladas.reduce((soma, valor) => soma + valor, 0n);
  if (total <= 0n || rateada <= 0n) return [];

  const parciais = escaladas.map((escalada, posicao) => {
    const bruto = escalada * CEM_ESCALADO;
    return { posicao, indice: bruto / total, resto: bruto % total };
  });

  /*
    O alvo é o que o conjunto **vale** em índice, arredondado ao 0,0001% mais
    próximo — e é `CEM_ESCALADO` só quando as fatias cobrem a linha inteira. É a
    mesma conta de `indicesPorPeso`, que resolve o mesmo problema para o título.
  */
  const alvo = (rateada * CEM_ESCALADO + total / 2n) / total;

  let sobra = alvo - parciais.reduce((soma, parcial) => soma + parcial.indice, 0n);

  const porResto = [...parciais].sort((a, b) => {
    if (a.resto !== b.resto) return a.resto > b.resto ? -1 : 1;
    return a.posicao - b.posicao;
  });

  for (const parcial of porResto) {
    if (sobra <= 0n) break;
    parcial.indice += 1n;
    sobra -= 1n;
  }

  emprestarParaAsFatiasZeradas(parciais);

  return parciais.map((parcial) => desescalar(parcial.indice, CASAS_PERCENTUAL));
}

/**
 * Levanta a fatia que arredondou para zero, tirando da maior.
 *
 * Fora de `indicesDoRateioPorQuantidade` porque as duas varreduras somadas às
 * dela passavam o limite de complexidade do lint. Altera `parciais` no lugar: o
 * chamador é quem tem a lista, e devolver uma cópia obrigaria a reordená-la.
 *
 * Desiste em silêncio se **a maior também é 1**, que só acontece com mais fatias
 * do que centésimos de milésimo a repartir — `MAXIMO_RATEIOS_ITEM_NOTA` é 20, e
 * 100% são um milhão deles. A guarda existe para o laço não roubar de quem não
 * tem.
 */
function emprestarParaAsFatiasZeradas(parciais: { indice: bigint }[]): void {
  for (const parcial of parciais) {
    if (parcial.indice > 0n) continue;

    const maior = parciais.reduce((a, b) => (b.indice > a.indice ? b : a));
    if (maior.indice <= 1n) return;

    maior.indice -= 1n;
    parcial.indice = 1n;
  }
}

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

const quantidadeDoItemSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade deve ser maior que zero.',
);

/**
 * Valor que não pode ser negativo — o unitário, o de outros e o desconto.
 *
 * Desconto negativo seria acréscimo escondido, e acréscimo tem campo próprio
 * ("outros"). Sem esta recusa, a mesma linha poderia ser digitada de duas formas
 * que somam igual, e o relatório que separa desconto de despesa acessória
 * passaria a medir a preferência de quem digitou.
 */
const valorNaoNegativoSchema = dinheiroSchema.refine(
  (v) => Number(v) >= 0,
  'O valor não pode ser negativo.',
);

/**
 * O **preço unitário**, que tem seis casas e não duas.
 *
 * Separado do acima porque a escala é outra: o unitário é preço por unidade e
 * aceita `6.123456` — que é como a nota do posto de combustível o traz —,
 * enquanto "outros", frete e desconto são dinheiro do documento e continuam em
 * centavos. Ver `precoUnitarioSchema`.
 */
const precoNaoNegativoSchema = precoUnitarioSchema.refine(
  (v) => Number(v) >= 0,
  'O preço não pode ser negativo.',
);

const rateiosSchema = z
  .array(rateioDoItemDeNotaEntradaSchema)
  .max(MAXIMO_RATEIOS_ITEM_NOTA, `O item tem no máximo ${MAXIMO_RATEIOS_ITEM_NOTA} rateios.`)
  .prefault([]);

/**
 * Item no payload de gravação.
 *
 * **Não tem `valorInsumo`, `valorTotal` nem `valorCustoEstoque`.** Os três são
 * conta sobre os outros campos, e aceitá-los deixaria o cliente gravar um custo
 * que não bate com o que ele mesmo mandou.
 *
 * **Não tem os três créditos.** Eles são apurados a partir da operação fiscal do
 * item e do regime da empresa, e digitados permitiriam creditar ICMS numa compra
 * de empresa do Simples.
 *
 * **Não tem tipo de movimentação de estoque**, embora `.spec/010` liste "ID Tipo
 * de Movimento" no item. Quem o carrega é a **operação fiscal**
 * (`operacoes_fiscais.tipo_movimentacao_estoque_id`), que é obrigada a tê-lo
 * quando atualiza estoque. Pedi-lo também aqui abriria a chance de a nota
 * registrar a entrada sob um motivo que contradiz a operação escolhida.
 *
 * **Não tem `id`.** Os itens são regravados por inteiro a cada `PUT`, como no
 * pedido e pela mesma razão.
 */
export const itemNotaCompraEntradaSchema = z.object({
  /**
   * O insumo, e **não** a linha de `insumos_empresa` que o banco guarda.
   *
   * Mesma escolha do item do pedido: pedir o vínculo ao cliente obrigaria a tela
   * a abrir o cadastro de cada insumo para descobrir o id, e o primeiro que
   * errasse o par lançaria material na empresa errada sem que nada acusasse.
   */
  insumoId: uuidSchema,
  /**
   * O saldo de pedido que esta linha atende. Opcional.
   *
   * Nulo é a nota **sem pedido prévio** — a compra de balcão, o material que o
   * fornecedor mandou a mais, o serviço contratado por telefone. Preenchido, é o
   * que faz o pedido baixar: `quantidade_atendida` do item de pedido é
   * recalculada a partir das notas que apontam para ele, e é isso que tira o
   * pedido da tela de entrada por pedido.
   *
   * A derivação segue o mesmo desenho do par solicitação × pedido: a nota
   * **pendente** já conta como atendimento, porque a mercadoria chegou — o que a
   * confirmação acrescenta é efeito de estoque e financeiro, não recebimento. E
   * é o que impede duas notas para o mesmo saldo.
   */
  pedidoCompraItemId: uuidSchema.nullable().prefault(null),
  /**
   * Para onde a entrada vai. **Por item, e não por nota** (`.spec/016`, seção 8):
   * um caminhão descarrega o cimento no canteiro e a ferramenta no almoxarifado
   * central.
   */
  depositoId: uuidSchema,
  /**
   * A operação fiscal **desta linha**, que prevalece sobre a da nota
   * (`.spec/010`).
   *
   * Obrigatória, e não "vazio herda a do cabeçalho": a operação decide se o item
   * movimenta estoque e quanto imposto sai do custo, e um valor herdado em
   * silêncio faria a mesma nota se comportar de dois jeitos conforme o campo
   * tivesse sido tocado. Quem preenche com a operação da nota é a **tela**, ao
   * criar a linha — e ali a escolha fica visível.
   */
  operacaoFiscalId: uuidSchema,
  /**
   * Em que conta contábil **esta linha** se classifica.
   *
   * É daqui que sai o índice contábil do título gerado na confirmação: a nota
   * cujas linhas são cimento, furadeira e serviço tem três classificações e uma
   * parcela só, e por isso a conta é do item e não do título.
   *
   * **Opcional no schema e obrigatória na API** quando a operação fiscal da nota
   * gera financeiro. A distinção não cabe aqui: o schema conhece o *id* da
   * operação, não a flag dela, e adivinhar pelo id seria inventar. Quem cobra é o
   * servidor, que já leu o cadastro — e a mensagem chega presa à linha.
   *
   * Quem preenche é a **tela**, com a conta sugerida pela operação fiscal da
   * linha, do mesmo jeito que preenche a operação com a operação padrão da nota.
   * Herdar em silêncio no servidor faria a mesma nota classificar de dois jeitos
   * conforme o campo tivesse sido tocado, e o balancete não teria como explicar.
   */
  contaContabilId: z
    .union([uuidSchema, z.literal(''), z.null()])
    .transform((valor) => (valor === '' || valor === null ? null : valor))
    .prefault(null),
  /**
   * A quantidade **na unidade do insumo**.
   *
   * Quando houve conversão de unidade, é o resultado dela — e o número impresso
   * no documento fica nos três campos logo abaixo. É esta que movimenta estoque,
   * e é ela que o `apenas_inteiros` da unidade julga.
   */
  quantidade: quantidadeDoItemSchema,
  /**
   * A quantidade **como o documento a traz**. Nula quando não houve conversão.
   *
   * Os três campos de documento — este, a unidade e o fator — andam juntos:
   * nenhum ou todos, e quem cobra é `validarDocumentoDosItens`. Sozinha, a
   * quantidade convertida não distingue conversão legítima de digitação errada
   * (`.spec/015`, decisão 20).
   */
  quantidadeDocumento: quantidadeDoItemSchema.nullable().prefault(null),
  /** A unidade em que o documento veio. Nula junto com a quantidade acima. */
  unidadeDocumentoId: uuidSchema.nullable().prefault(null),
  /**
   * O fator **aplicado nesta linha**, e não o que o cadastro do fornecedor tem
   * hoje.
   *
   * É a diferença que faz a coluna valer: o de‑para é editável, e sem o fator
   * gravado a nota do ano passado passaria a ser explicada por um número que não
   * foi o usado nela.
   *
   * Chega como **um decimal**, e não como a razão de `fornecedores_itens`: aqui
   * ele é registro do que aconteceu, não a fonte da conta. Quem dividiu foi
   * `converterQuantidade`, com a razão exata, antes de a linha chegar ao payload.
   */
  fatorConversao: fatorConversaoSchema.nullable().prefault(null),
  /** Preço de uma unidade — **seis casas**, como a nota do posto o traz. */
  valorUnitario: precoNaoNegativoSchema,
  /** Despesas acessórias cobradas à parte pelo fornecedor — embalagem, montagem. */
  valorOutros: valorNaoNegativoSchema.prefault('0'),
  /**
   * O que o fornecedor cobrou pelo transporte **desta linha**. Só no frete `CIF`.
   *
   * Por item, e não no cabeçalho como era: é assim que o pedido já o registra, e
   * é o que faz o custo de cada material sair pronto da linha. Antes o frete era
   * um número único do documento, que não entrava no custo de estoque de item
   * nenhum — quem o quisesse ali tinha de digitá-lo em "outros", perdendo a
   * distinção entre transporte e despesa acessória.
   *
   * Campo próprio, e não uma parcela de "outros", porque é o que se negocia
   * separado e é o que se confere contra a linha de `vFrete` do documento.
   *
   * Zero fora do `CIF`, e quem recusa é `validarFreteDosItens`: a modalidade mora
   * no cabeçalho, então o `CHECK` do banco não alcança o par a partir daqui.
   */
  valorFrete: valorNaoNegativoSchema.prefault('0'),
  valorDesconto: valorNaoNegativoSchema.prefault('0'),
  impostos: impostosDoItemDeNotaEntradaSchema.prefault({}),
  /**
   * Como o custo deste item se divide entre obras. Pode ser vazio.
   *
   * Vazio significa "não se divide": o material entra no depósito do item e o
   * consumo dirá depois para qual obra foi.
   */
  rateios: rateiosSchema,
  observacao: z.string().trim().max(500, 'A observação tem no máximo 500 caracteres.').optional(),
});

export type ItemNotaCompraEntrada = z.infer<typeof itemNotaCompraEntradaSchema>;

/**
 * O crédito destacado nunca passa do imposto destacado.
 *
 * A regra é do banco (`CHECK valor_credito_icms <= valor_icms`), e ela vale
 * sozinha porque o crédito é sempre **o próprio imposto ou zero** —
 * `valoresDoItemDeNota` não sabe produzir outra coisa. Não há validação a
 * escrever aqui, e o comentário existe para que a próxima pessoa não vá procurá-la.
 */

/**
 * Os três campos de documento andam juntos: nenhum ou todos.
 *
 * A linha meio preenchida é a única que ninguém consegue interpretar depois —
 * "44,4444 PC convertidos de 1.000 de quê, por qual fator?". O `CHECK` do banco
 * cobre o mesmo caso (as três colunas estão na mesma linha); aqui a mensagem
 * chega **presa ao campo**, que é o que permite à tela destacá-lo.
 *
 * A regra é sobre a **presença**, não sobre o valor: se o fator confere com as
 * duas quantidades, este contrato não julga. Quem faz a conta é
 * `converterQuantidade`, e refazê-la aqui recusaria a linha que o conferente
 * ajustou de propósito contra o papel — que é o caso em que a nota diverge do
 * pedido, e o motivo de a tela não gravar direto.
 */
function validarDocumentoDosItens(itens: ItemNotaCompraEntrada[], ctx: z.RefinementCtx): void {
  itens.forEach((item, indice) => {
    const informados = [
      item.quantidadeDocumento !== null,
      item.unidadeDocumentoId !== null,
      item.fatorConversao !== null,
    ].filter(Boolean).length;

    if (informados === 0 || informados === 3) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'quantidadeDocumento'],
      message:
        'A conversão de unidade precisa dos três campos do documento: quantidade, unidade e fator. Com um deles vazio, ninguém consegue explicar depois de onde veio a quantidade gravada.',
    });
  });
}

/** O rateio de cada item cabe na quantidade da linha, com a mensagem presa a ela. */
function validarRateiosDosItens(itens: ItemNotaCompraEntrada[], ctx: z.RefinementCtx): void {
  itens.forEach((item, indice) => {
    if (rateioCabeNaQuantidade(item.quantidade, item.rateios)) return;

    const soma = somarQuantidade(item.rateios.map((rateio) => rateio.quantidade));
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'rateios'],
      message: `As fatias somam ${soma} e a linha só tem ${item.quantidade}. O rateio não pode distribuir mais material do que entrou — o que sobrar sem fatia entra no depósito do item.`,
    });
  });
}

/**
 * A mesma obra, etapa **e depósito** duas vezes no rateio de um item é digitação
 * repetida.
 *
 * Duas fatias de 6 e 4 sacos para a Residencial Aurora, no mesmo canteiro, são
 * uma de 10 — mantê-las separadas gera duas entradas idênticas no extrato do
 * depósito, que ninguém concilia depois.
 *
 * **O depósito entrou na chave** quando a fatia passou a ter um, e foi o que
 * deixou de ser recusado: seis sacos no canteiro da Aurora e quatro no
 * almoxarifado, ambos da Aurora, são divisão legítima — é o que o rateio do
 * pedido já dizia, e é o que a nota passou a saber representar. Antes a regra
 * era só obra × etapa, e não podia ser outra coisa: sem depósito, duas fatias da
 * mesma obra eram mesmo a mesma fatia escrita duas vezes.
 *
 * O contas a pagar não ganha linha repetida por isso: `rateioDeObraDoTitulo` soma
 * as fatias por obra e etapa antes de montar o rateio do título. A etapa segue na
 * chave pelo motivo de sempre — é como o custo de fundação se separa do de
 * alvenaria.
 */
function validarObrasDistintas(itens: ItemNotaCompraEntrada[], ctx: z.RefinementCtx): void {
  itens.forEach((item, indice) => {
    const vistos = new Set<string>();

    item.rateios.forEach((rateio, posicao) => {
      const chave = `${rateio.obraId}|${rateio.obraParteId ?? ''}|${rateio.depositoId}`;
      if (!vistos.has(chave)) {
        vistos.add(chave);
        return;
      }

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [indice, 'rateios', posicao, 'obraId'],
        message:
          'Esta obra já tem fatia neste depósito, nesta linha. Some as quantidades numa fatia só, ou aponte a segunda para outro depósito.',
      });
    });
  });
}

/**
 * O mesmo saldo de pedido em duas linhas é sempre digitação repetida.
 *
 * As duas somariam atendimento no mesmo item de pedido, e o saldo em aberto
 * cairia em dobro — o pedido apareceria atendido tendo recebido metade.
 *
 * **Não há recusa por insumo × depósito**, ao contrário da solicitação: a mesma
 * nota traz o mesmo material em dois lotes de preços diferentes, e recusar
 * obrigaria a lançar duas notas para um documento só.
 */
function validarPedidosDistintos(itens: ItemNotaCompraEntrada[], ctx: z.RefinementCtx): void {
  const vistos = new Set<string>();

  itens.forEach((item, indice) => {
    const origem = item.pedidoCompraItemId;
    if (origem === null) return;

    if (vistos.has(origem)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [indice, 'pedidoCompraItemId'],
        message:
          'Este item de pedido já está sendo atendido em outra linha. Some as quantidades numa linha só.',
      });
    }
    vistos.add(origem);
  });
}

const itensSchema = z
  .array(itemNotaCompraEntradaSchema)
  .min(1, 'A nota precisa de pelo menos um item.')
  .max(MAXIMO_ITENS_NOTA, `A nota tem no máximo ${MAXIMO_ITENS_NOTA} itens.`)
  .superRefine(validarDocumentoDosItens)
  .superRefine(validarRateiosDosItens)
  .superRefine(validarObrasDistintas)
  .superRefine(validarPedidosDistintos);

/** Item como a API o devolve, com os nomes resolvidos e os valores prontos. */
export const itemNotaCompraSchema = z.object({
  id: uuidSchema,
  /** O vínculo gravado. Vem na leitura porque a movimentação de estoque aponta para ele. */
  insumoEmpresaId: uuidSchema,
  /** O insumo por trás do vínculo — é ele que o formulário devolve no `PUT`. */
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  /**
   * Material ou serviço — é o que decide **quais impostos** a linha pergunta.
   *
   * Por item, e não pelo documento: a mesma nota traz cimento e a locação da
   * bomba de concretagem, e as duas linhas destacam tributos diferentes.
   * `blocosDeImposto` recebe este valor.
   *
   * Desnormalizado na leitura, e não buscado pela tela, pela mesma razão do item
   * do pedido: o formulário abriria uma consulta por item só para saber quais
   * campos desenhar, e o `SELECT` dos itens já junta `insumos` para o nome.
   */
  insumoTipo: tipoInsumoSchema,
  unidadeSigla: z.string().nullable(),
  /**
   * Casas do preço daquele insumo, desnormalizadas como a sigla e pelo mesmo
   * motivo: quem mostra a linha precisa da escala sem uma consulta por item.
   *
   * A listagem resume cada linha como "5.000 L × 6,123456 = 30.617,28", e com
   * duas casas fixas o unitário do combustível apareceria arredondado ao lado de
   * um total que não bate com ele — numa tela que existe para conferir a nota
   * contra o papel do fornecedor.
   */
  casasDecimaisPreco: z.number().int(),
  /** O saldo de pedido que esta linha atendeu. Nulo em compra sem pedido. */
  pedidoCompraItemId: uuidSchema.nullable(),
  /** O pedido daquele item, resolvido — é ele que a tela sabe abrir. */
  pedidoCompraId: uuidSchema.nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  operacaoFiscalId: uuidSchema,
  operacaoFiscalNome: z.string().nullable(),
  /**
   * As três flags da operação daquela linha, resolvidas.
   *
   * Vêm na leitura porque são elas que explicam o custo gravado: sem
   * `creditaIcms` à vista, o item cujo custo ficou abaixo do valor total pareceria
   * erro de conta. E são as **da operação hoje**, não as do dia da nota — o
   * crédito congelado está nos valores, não aqui.
   */
  operacaoAtualizaEstoque: z.boolean(),
  operacaoGeraFinanceiro: z.boolean(),
  /** A classificação gravada nesta linha. Nula nas notas anteriores à coluna. */
  contaContabilId: uuidSchema.nullable(),
  contaContabilDescricao: z.string().nullable(),
  quantidade: quantidadeSchema,
  /**
   * O que o documento dizia, quando houve conversão de unidade — os três nulos
   * é o caso comum (`.spec/015`, decisão 20).
   *
   * A sigla vem resolvida, como a do insumo e pelo mesmo motivo: a conferência
   * mostra "1.000 KG → 44,4444 SC", e um uuid ali não diz nada. Nula junto com
   * as outras duas.
   */
  quantidadeDocumento: quantidadeSchema.nullable(),
  unidadeDocumentoId: uuidSchema.nullable(),
  unidadeDocumentoSigla: z.string().nullable(),
  fatorConversao: z.string().nullable(),
  /** Seis casas: o preço é do fornecedor. O produto dele é que é dinheiro. */
  valorUnitario: precoUnitarioSchema,
  valorInsumo: dinheiroSchema,
  valorOutros: dinheiroSchema,
  /** Cobrado pelo fornecedor no `CIF`. Zero nas outras modalidades. */
  valorFrete: dinheiroSchema,
  valorDesconto: dinheiroSchema,
  valorTotal: dinheiroSchema,
  valorCustoEstoque: dinheiroSchema,
  impostos: impostosDoItemDeNotaSchema,
  rateios: z.array(rateioDoItemDeNotaSchema),
  observacao: z.string().nullable(),
});

export type ItemNotaCompra = z.infer<typeof itemNotaCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Financeiro da nota                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Uma parcela do que se deve ao fornecedor (`.spec/010`, "Financeiro da Nota de
 * Compra").
 *
 * É a tabela de onde o contas a pagar nasce quando a nota é confirmada. Ela
 * existe **separada da condição de pagamento** porque o que a nota traz é o que
 * foi combinado de fato: o fornecedor concede um dia a mais no vencimento, ou
 * arredonda a última parcela, e a condição continua sendo "30/60/90". Derivar os
 * títulos da condição no momento da confirmação faria a nota gerar vencimentos
 * que não estão no documento que o financeiro vai pagar.
 *
 * A tela **sugere** as parcelas a partir da condição escolhida — ver
 * `sugerirParcelas` — e o usuário ajusta o que for preciso.
 */
export const parcelaDaNotaEntradaSchema = z.object({
  parcela: z
    .number()
    .int('O número da parcela é inteiro.')
    .min(1, 'A numeração das parcelas começa em 1.')
    .max(MAXIMO_PARCELAS_NOTA),
  vencimento: dataSchema,
  valor: dinheiroSchema.refine((v) => Number(v) > 0, 'O valor da parcela deve ser maior que zero.'),
});

export type ParcelaDaNotaEntrada = z.infer<typeof parcelaDaNotaEntradaSchema>;

/** Parcela como a API a devolve. */
export const parcelaDaNotaSchema = z.object({
  id: uuidSchema,
  parcela: z.number().int(),
  vencimento: dataSchema,
  valor: dinheiroSchema,
});

export type ParcelaDaNota = z.infer<typeof parcelaDaNotaSchema>;

/**
 * Numeração sequencial a partir de 1, e vencimento que nunca anda para trás.
 *
 * A mesma regra da condição de pagamento, e pelo mesmo motivo: a parcela é a
 * **posição** no parcelamento, e um conjunto `1, 2, 4` faria o contas a pagar
 * emitir três títulos numerados como quatro. Dois vencimentos no mesmo dia
 * continuam aceitos — entrada em dinheiro mais boleto no ato existe.
 */
function validarSequenciaDasParcelas(parcelas: ParcelaDaNotaEntrada[], ctx: z.RefinementCtx): void {
  parcelas.forEach((atual, indice) => {
    if (atual.parcela !== indice + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [indice, 'parcela'],
        message: `As parcelas são numeradas em sequência a partir de 1. Esta deveria ser a ${indice + 1}ª.`,
      });
      return;
    }

    const anterior = parcelas[indice - 1];
    if (anterior && atual.vencimento < anterior.vencimento) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [indice, 'vencimento'],
        message: 'O vencimento não pode ser anterior ao da parcela anterior.',
      });
    }
  });
}

/**
 * As parcelas, que podem **não existir** (`.spec/015`, decisão 24).
 *
 * O `.min(1)` saiu daqui, e o motivo é o mesmo da condição de pagamento: a nota
 * de bonificação, remessa ou transferência não deve nada, e exigir uma parcela
 * dela obrigava a inventar um vencimento que ninguém vai pagar — pior que campo
 * vazio, porque alguém confere contra ele.
 *
 * **Vazia não virou "vale sempre".** Quem decide é a operação fiscal do
 * cabeçalho: gerando financeiro, o service exige ao menos uma parcela; não
 * gerando, recusa qualquer uma. A checagem não cabe aqui porque depende de outra
 * tabela — ver `condicaoPagamentoId`.
 */
const parcelasSchema = z
  .array(parcelaDaNotaEntradaSchema)
  .max(MAXIMO_PARCELAS_NOTA, `A nota tem no máximo ${MAXIMO_PARCELAS_NOTA} parcelas.`)
  .superRefine(validarSequenciaDasParcelas);

/**
 * Sugere as parcelas a partir de um valor, de uma data base e dos prazos da
 * condição de pagamento.
 *
 * A sobra de arredondamento vai para a **última** parcela, de modo que a soma
 * bata exatamente com o valor — que é a invariante que `validarSomaDasParcelas`
 * cobra depois. Não há critério de "maior resto" como no rateio: as parcelas são
 * sequenciais e o mercado espera a diferença no final.
 *
 * É sugestão, e só: a **API não gera parcela nenhuma**, ela apenas confere a
 * soma. Quem lança a nota tem o documento na mão, e o documento manda.
 *
 * A data entra e sai como `AAAA-MM-DD`, e a soma de dias é feita em UTC de
 * propósito: `new Date('2026-03-10')` no fuso de São Paulo já nasce no dia 9 às
 * 21h, e somar 30 dias sobre a data local devolveria o dia errado nas viradas de
 * horário de verão.
 */
export function sugerirParcelas(
  valorTotal: string,
  dataBase: string,
  prazos: readonly { parcela: number; qtdDias: number }[],
): ParcelaDaNotaEntrada[] {
  if (prazos.length === 0) return [];

  const ordenados = [...prazos].sort((a, b) => a.parcela - b.parcela);
  const total = escalar(valorTotal, CASAS_DINHEIRO);
  const porParcela = dividirArredondando(total, BigInt(ordenados.length));

  return ordenados.map((prazo, indice) => {
    const ultima = indice === ordenados.length - 1;
    const valor = ultima ? total - porParcela * BigInt(ordenados.length - 1) : porParcela;

    return {
      parcela: prazo.parcela,
      vencimento: somarDias(dataBase, prazo.qtdDias),
      valor: desescalar(valor < 0n ? 0n : valor, CASAS_DINHEIRO),
    };
  });
}

/** `AAAA-MM-DD` mais `dias`, em UTC. Ver o comentário de `sugerirParcelas`. */
export function somarDias(data: string, dias: number): string {
  const instante = new Date(`${data}T00:00:00.000Z`);
  if (Number.isNaN(instante.getTime())) return data;

  instante.setUTCDate(instante.getUTCDate() + dias);
  return instante.toISOString().slice(0, 10);
}

/* -------------------------------------------------------------------------- */
/* Conferência do parcelamento contra a condição                              */
/* -------------------------------------------------------------------------- */

/**
 * A folga, em dias, entre o vencimento lançado e o que a condição prevê
 * (`.spec/015`, decisão 25).
 *
 * **Três, e não zero.** A decisão 19 já registrava o motivo: o fornecedor concede
 * um dia a mais no vencimento, joga o boleto que cairia no sábado para a segunda,
 * ou arredonda a última parcela — e a condição continua sendo "30/60/90". Exigir
 * a data exata transformaria cada uma dessas notas legítimas numa recusa, e o
 * conserto seria mentir num dos dois lados: corrigir o vencimento que o boleto
 * traz, ou cadastrar uma condição "31/61/91" que não existe.
 *
 * **E não é tolerância larga.** Três dias não alcançam o erro que esta
 * conferência existe para achar: 30/60/90 lançado como 15/30/45, ou como
 * 30/60/90 a partir do lançamento em vez da emissão numa nota que chegou uma
 * semana depois.
 */
export const FOLGA_DE_VENCIMENTO_EM_DIAS = 3;

/** Distância em dias entre duas datas `AAAA-MM-DD`, em UTC. */
function diasEntre(a: string, b: string): number {
  const umDia = 24 * 60 * 60 * 1000;
  const inicio = new Date(`${a}T00:00:00.000Z`).getTime();
  const fim = new Date(`${b}T00:00:00.000Z`).getTime();

  if (Number.isNaN(inicio) || Number.isNaN(fim)) return Number.POSITIVE_INFINITY;

  return Math.abs(fim - inicio) / umDia;
}

/** O que a conferência achou de errado. `null` é "confere". */
export type DivergenciaDoParcelamento =
  /** A condição prevê N parcelas e a nota tem outra quantidade. */
  | { code: 'quantidade'; previstas: number; lancadas: number }
  /** Uma parcela vence fora da folga. Traz a primeira, que é onde se corrige. */
  | { code: 'vencimento'; parcela: number; previsto: string; lancado: string };

/**
 * O parcelamento lançado corresponde ao que a condição prevê, contado da data de
 * emissão? (`.spec/015`, decisão 25.)
 *
 * **A base é a emissão, e não o lançamento.** É a data do documento, e é dela que
 * o fornecedor conta o prazo que negociou; o lançamento é quando a nota chegou à
 * mesa do conferente, e uma nota emitida em 30 de março e lançada em 8 de abril
 * teria, por essa conta, nove dias a mais em toda parcela — a conferência
 * acusaria toda nota que demorou a chegar, que é justamente a que não tem
 * problema nenhum.
 *
 * Devolve **uma** divergência, na ordem em que se resolve: quantidade primeiro,
 * porque com o número errado de parcelas comparar datas apontaria a linha errada.
 * Depois a primeira parcela fora da folga — mostrar as três de uma vez, numa nota
 * em que o conferente errou a data base, seria repetir o mesmo engano três vezes.
 *
 * `null` também quando não há o que conferir: sem prazos cadastrados na condição,
 * ou sem parcela nenhuma. Não é omissão — é a nota que não gera financeiro, e a
 * ausência de parcelas ali já foi decidida em outro lugar (decisão 24).
 *
 * Função **pura**, no contrato, e não no service: é a mesma conta que a tela faz
 * para avisar antes de o botão recusar, e duas cópias divergiriam na primeira
 * correção da folga.
 */
export function divergenciaDoParcelamento(
  dataEmissao: string,
  prazos: readonly { parcela: number; qtdDias: number }[],
  parcelas: readonly { parcela: number; vencimento: string }[],
): DivergenciaDoParcelamento | null {
  if (prazos.length === 0 || parcelas.length === 0) return null;

  if (prazos.length !== parcelas.length) {
    return { code: 'quantidade', previstas: prazos.length, lancadas: parcelas.length };
  }

  // As duas listas são ordenadas pela posição antes de se compararem: a ordem de
  // leitura do banco não é promessa nenhuma, e comparar a 1ª contra a 3ª acusaria
  // um parcelamento correto.
  const previstos = [...prazos].sort((a, b) => a.parcela - b.parcela);
  const lancadas = [...parcelas].sort((a, b) => a.parcela - b.parcela);

  for (const [indice, prazo] of previstos.entries()) {
    const lancada = lancadas[indice];
    if (lancada === undefined) continue;

    const previsto = somarDias(dataEmissao, prazo.qtdDias);
    if (diasEntre(previsto, lancada.vencimento) <= FOLGA_DE_VENCIMENTO_EM_DIAS) continue;

    return { code: 'vencimento', parcela: lancada.parcela, previsto, lancado: lancada.vencimento };
  }

  return null;
}

/* -------------------------------------------------------------------------- */
/* Totais da nota                                                             */
/* -------------------------------------------------------------------------- */

/**
 * O valor da mercadoria: a soma dos totais líquidos dos itens.
 *
 * **Não é o valor do documento** — falta o que é cobrado por fora. É o número que
 * se confere contra o "valor dos produtos" da NF-e.
 */
export function valorDaMercadoriaDaNota(itens: readonly { valorTotal: string }[]): string {
  return somarDinheiro(itens.map((item) => item.valorTotal));
}

/**
 * O que se deve ao fornecedor: mercadoria, mais IPI e ICMS ST de cada item.
 *
 * É este o número que as parcelas do financeiro têm de somar, e é ele que se
 * confere contra o total da NF-e. O custo de estoque **não serve** para isso: ele
 * já desconta os créditos, e crédito reduz o custo do material, não o que se
 * paga.
 *
 * A conta dos impostos é por item, e não sobre o total: cada linha tem a sua
 * tributação, e somá-los à parte daria o mesmo resultado por acaso enquanto todas
 * as linhas fossem da mesma operação fiscal.
 *
 * **O frete não é mais parcela do documento.** Ele era somado aqui, à parte dos
 * itens, enquanto morava no cabeçalho; agora está dentro do `valorTotal` de cada
 * linha, e somá-lo de novo contaria o transporte duas vezes. É a mesma mudança
 * que `totalDoPedido` já tinha recebido, e pelo mesmo motivo.
 */
export function valorAPagarDaNota(
  itens: readonly {
    valorTotal: string;
    impostos: { valorIpi: string; valorIcmsSt: string };
  }[],
): string {
  return somarDinheiro(
    itens.flatMap((item) => [item.valorTotal, item.impostos.valorIpi, item.impostos.valorIcmsSt]),
  );
}

/** O que entrou no estoque, somado — informativo, para o rodapé da tela. */
export function custoDeEstoqueDaNota(itens: readonly { valorCustoEstoque: string }[]): string {
  return somarDinheiro(itens.map((item) => item.valorCustoEstoque));
}

/** A soma das parcelas do financeiro. */
export function somaDasParcelas(parcelas: readonly { valor: string }[]): string {
  return somarDinheiro(parcelas.map((parcela) => parcela.valor));
}

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Contexto de cálculo sem crédito nenhum.
 *
 * Usado onde só interessam `valorInsumo` e `valorTotal`, que não dependem de
 * crédito — a validação da soma das parcelas é o caso. Existe nomeado para que
 * nenhuma chamada passe flags inventadas achando que elas não têm efeito: no dia
 * em que a fórmula do total passar a olhá-las, o nome é o que denuncia o lugar.
 */
const OPERACAO_SEM_CREDITO = {
  operacao: { creditaIcms: false, creditaPis: false, creditaCofins: false },
  regimeEmpresa: null,
} as const;

/** O imposto por dentro que não cabe na linha, com os dois números da conta. */
export interface ExcessoDeImpostoPorDentro {
  /** O que se deve pela linha: mercadoria líquida + IPI + ICMS ST. */
  valorAPagar: string;
  /** O imposto cobrado por dentro — hoje, o ICMS destacado. */
  valorPorDentro: string;
}

/**
 * O imposto cobrado **por dentro** passa do que se deve pela linha?
 *
 * Devolve os dois números quando passa, e `null` quando cabe.
 *
 * **Por dentro** quer dizer embutido no preço: o fornecedor não o acrescenta ao
 * que cobra, ele já está dentro. Daí a impossibilidade aritmética — um ICMS de
 * R$ 2.000,00 não cabe dentro de uma linha de R$ 1.000,00. É o mesmo imposto que
 * vira crédito quando a operação e o regime permitem, e é por isso que a recusa
 * fecha o buraco que o relato descreve: `crédito ≤ imposto destacado ≤ valor a
 * pagar`, então o custo de estoque nunca mais chega a negativo.
 *
 * O teto é o **valor a pagar**, e não a mercadoria líquida, embora a base do ICMS
 * quase nunca alcance o IPI e nunca alcance o ICMS ST. É o teto mais generoso que
 * ainda fecha o buraco, e a escolha é deliberada: esta recusa aparece sobre uma
 * nota que o conferente está digitando com o papel do fornecedor na frente, e
 * recusar um documento legítimo por causa da base de cálculo é pior do que deixar
 * passar um exagero que ainda cabe no total. Quem confere a base contra a
 * alíquota é o bloco de impostos da tela, campo a campo.
 *
 * Só o ICMS por hoje. PIS e COFINS por dentro saíram do desenho da nota — ver
 * `valoresDoItemDeNota` —, e quando voltarem é aqui que a soma cresce, junto com
 * o crédito que eles formam.
 */
export function excessoDeImpostoPorDentro(item: {
  quantidade: string;
  valorUnitario: string;
  valorOutros: string;
  valorFrete?: string | undefined;
  valorDesconto: string;
  impostos: { valorIcms: string; valorIcmsSt: string; valorIpi: string };
}): ExcessoDeImpostoPorDentro | null {
  const { valorAPagar } = valoresDoItemDeNota(item, OPERACAO_SEM_CREDITO);
  const valorPorDentro = item.impostos.valorIcms;

  // Sobre inteiros escalados, como toda comparação de dinheiro daqui: `Number`
  // empataria valores que não empatam e deixaria passar o centavo de excesso.
  if (compararDinheiro(valorPorDentro, valorAPagar) <= 0) return null;

  return { valorAPagar, valorPorDentro };
}

/**
 * Chave da NF-e no formulário: vazio, nulo e ausente são a mesma coisa.
 *
 * A string vazia do `<input>` entra e sai como `null`. Sem isto, "não digitei a
 * chave" chegaria como `''` e falharia com uma mensagem sobre os 44 dígitos —
 * sobre um campo que é opcional.
 */
const chaveNfeDoPayloadSchema = z
  .union([chaveNfeSchema, z.literal(''), z.null()])
  .transform((valor) => (valor === '' || valor === null ? null : valor))
  .prefault(null);

/**
 * Payload de gravação.
 *
 * **Não tem `situacao` nem `confirmadaEm`.** A nota nasce `PENDENTE`, e confirmar
 * é ato próprio, com rota própria: ele verifica estoque, custo e financeiro antes
 * de decidir, e um `PATCH {situacao}` convidaria o cliente a tentar o valor
 * automático.
 *
 * **Não tem valor total.** Ele é a soma dos itens, e aceitá-lo deixaria o cliente
 * declarar um total que não bate com as linhas que ele mesmo mandou. Quem soma é
 * `valorAPagarDaNota`, aqui, que é a mesma função que a tela usa.
 *
 * **Não tem obra.** A obra entra pelo **rateio do item**, como no pedido: uma nota
 * costuma trazer material para mais de uma obra no mesmo carregamento.
 */
const notaCompraBaseSchema = z.object({
  /**
   * Empresa do grupo que compra.
   *
   * É ela quem **define o regime tributário aplicado ao crédito** (`.spec/016`,
   * seção 8), e é por isso que trocá-la muda o custo de estoque de todos os itens
   * sem que nenhum campo de item tenha sido tocado.
   */
  empresaId: uuidSchema,
  /** De quem se comprou. `pessoas` com `is_fornecedor`. */
  fornecedorId: uuidSchema,
  /**
   * Número e série do documento do fornecedor, **copiados da nota**.
   *
   * São a diferença entre este documento e os outros dois do fluxo de compra: a
   * solicitação e o pedido recebem número do servidor, porque nascem aqui; a
   * nota traz o número de fora, e o sistema não tem nada a gerar.
   */
  numeroNota: numeroNotaSchema,
  serie: serieNotaSchema,
  /**
   * Em quantas vezes se paga. É dela que a tela sugere as parcelas.
   *
   * **Nula na nota que não gera financeiro** (`.spec/015`, decisão 24):
   * bonificação, remessa e transferência não se pagam em vez nenhuma.
   *
   * A exigência é condicional, e a condição está **fora deste payload** — em
   * `operacoes_fiscais.gera_financeiro` da operação do cabeçalho. Por isso o
   * contrato só afrouxa a forma, e quem cobra é o service, que carrega a
   * operação. É a mesma repartição da conta contábil do item, exigida só quando
   * a nota gera contas a pagar; o que o Zod alcança, o Zod recusa, e o que
   * depende de outra tabela é do service (`.spec/002`).
   */
  condicaoPagamentoId: uuidSchema.nullable().prefault(null),
  /**
   * A operação fiscal **padrão da nota** (`.spec/010`).
   *
   * Padrão, e não regra: cada item tem a sua, e é a do item que vale. Esta existe
   * para a tela preencher a linha nova sozinha e para a nota se classificar na
   * listagem — "quais notas são de compra para revenda" não se responde item a
   * item.
   */
  operacaoFiscalId: uuidSchema,
  /**
   * Chave de acesso da NF-e. Opcional.
   *
   * Nulo é a nota de fornecedor que não emite NF-e, ou o lançamento feito antes de
   * o arquivo chegar. Quando preenchida, é **única por empresa** — a mesma nota
   * lançada duas vezes duplicaria estoque e financeiro, e é o erro mais caro deste
   * módulo.
   */
  chaveNfe: chaveNfeDoPayloadSchema,
  /**
   * Data de emissão do documento — a **competência da apuração de créditos**
   * (`.spec/016`, seção 8).
   *
   * Separada da data de lançamento porque as duas divergem no caso normal: a nota
   * emitida em 30 de março chega no dia 2 de abril, e o crédito é de março
   * enquanto o estoque entra em abril.
   */
  dataEmissao: dataDoFatoSchema,
  /** Dia em que a nota entrou no sistema. Data de negócio, sem hora. */
  dataLancamento: dataDoFatoSchema,
  /**
   * Quem pagou o transporte (`.spec/010`). **A modalidade fica aqui; o valor, no
   * item.**
   *
   * A modalidade é do documento porque a nota é de um fornecedor só e um
   * carregamento só: "quem entrega" não muda de linha para linha. O valor desceu
   * para cada linha — ver `valorFrete` em `itemNotaCompraEntradaSchema`.
   */
  frete: freteNotaSchema.prefault('SEM'),
  itens: itensSchema,
  /**
   * As parcelas do que se deve. Havendo alguma, a soma tem de bater com o valor
   * do documento.
   *
   * Viajam no mesmo payload do cabeçalho e dos itens, e não numa rota própria:
   * uma nota **que se paga** gravada sem financeiro seria uma dívida sem
   * vencimento, e é exatamente essa linha que a geração de contas a pagar não
   * sabe usar.
   *
   * **Vazia na nota que não gera financeiro** (`.spec/015`, decisão 24) — ali
   * não há dívida a vencer. Quem separa os dois casos é a operação fiscal do
   * cabeçalho, no service. Ver `parcelasSchema`.
   */
  parcelas: parcelasSchema,
  /**
   * As parcelas vêm do pedido de compra, e não se editam na nota.
   *
   * Marcada pela entrada por pedido quando os pedidos atendidos têm uma condição
   * só. Com ela, o service **ignora** a condição e as parcelas do payload: a
   * condição é a dos pedidos, e as parcelas saem de `parcelasDoPedidoDaNota`
   * sobre o total e a emissão. Mudar o prazo é mudar o pedido.
   *
   * **Só a inclusão a lê.** Na alteração vale a gravada, e é isso que impede o
   * `PUT` de destravar a nota que nasceu travada.
   */
  parcelasDoPedido: z.boolean().prefault(false),
  observacao: z.string().trim().max(1000, 'A observação tem no máximo 1000 caracteres.').optional(),
});

/**
 * O que se deve ao fornecedor, calculado sobre o **payload** — o número que as
 * parcelas têm de somar.
 *
 * Exportada porque o service também precisa dele: na nota com
 * `parcelasDoPedido`, é ele quem monta as parcelas, e um total calculado de
 * outro jeito lá faria a soma recusada aqui.
 */
export function valorAPagarDaEntrada(nota: {
  itens: readonly ItemNotaCompraEntrada[];
  frete: FreteNota;
}): string {
  // Fora do CIF o frete da linha não conta, mesmo que o campo tenha vindo
  // preenchido: é a mesma regra que `validarFreteDosItens` recusa, e somá-lo
  // aqui produziria duas mensagens de erro sobre o mesmo engano.
  const comFrete = freteTemValor(nota.frete);

  return valorAPagarDaNota(
    nota.itens.map((item) => ({
      valorTotal: valoresDoItemDeNota(
        { ...item, valorFrete: comFrete ? item.valorFrete : '0' },
        OPERACAO_SEM_CREDITO,
      ).valorTotal,
      impostos: item.impostos,
    })),
  );
}

/**
 * O parcelamento da nota que nasceu do pedido de compra (`parcelasDoPedido`).
 *
 * Os prazos da condição do pedido, **contados da emissão** — a mesma base que a
 * confirmação confere (`divergenciaDoParcelamento`). Contar do lançamento, como
 * faz o botão "Sugerir da condição", travaria a nota emitida dias antes de
 * chegar: as parcelas não se editam, e a confirmação as recusaria.
 *
 * Condição sem prazos cadastrados é "à vista": uma parcela, na emissão. Total
 * zero não tem parcela — é a nota ainda sem itens, e a soma é conferida depois.
 *
 * Uma função só para a tela e o service: a tela mostra o que o service vai
 * gravar, e duas contas divergiriam no centavo da última parcela.
 */
export function parcelasDoPedidoDaNota(
  total: string,
  dataEmissao: string,
  prazos: readonly { parcela: number; qtdDias: number }[],
): ParcelaDaNotaEntrada[] {
  if (Number(total) <= 0) return [];

  const sugeridas = sugerirParcelas(total, dataEmissao, prazos);
  if (sugeridas.length > 0) return sugeridas;

  return [{ parcela: 1, vencimento: dataEmissao, valor: total }];
}

/**
 * A soma das parcelas é exatamente o valor do documento.
 *
 * Parcela a menos deixa dívida sem título; parcela a mais paga o que não se
 * comprou. É a mesma exigência de `.spec/010` para o rateio ("a soma dos valores
 * rateados deve ser sempre exatamente igual ao valor do documento"), aplicada ao
 * financeiro.
 *
 * A mensagem chega presa a `parcelas`, e não ao total: é a lista que o usuário
 * ajusta, e ela traz os dois números porque descobrir a diferença de cabeça, com
 * doze linhas na tela, é o que faz o usuário desistir e apagar tudo.
 *
 * A comparação é sobre inteiros escalados: doze parcelas de 83,33 somam
 * 999,9599999999999 em ponto flutuante, e o `===` sobre `Number` recusaria um
 * financeiro correto.
 */
function validarSomaDasParcelas(
  nota: {
    itens: ItemNotaCompraEntrada[];
    parcelas: ParcelaDaNotaEntrada[];
    frete: FreteNota;
  },
  ctx: z.RefinementCtx,
): void {
  /*
    Sem parcela nenhuma não há soma a conferir.

    É a nota que não gera financeiro (decisão 24), e sem esta saída ela seria
    recusada com "as parcelas somam 0,00 e a nota vale 345,00" — uma acusação
    sobre uma lista que **deve** estar vazia. Quem decide se o vazio cabe é o
    service, que conhece a operação fiscal; aqui só se sabe que não há o que
    somar.
  */
  if (nota.parcelas.length === 0) return;

  const devido = valorAPagarDaEntrada(nota);
  const somado = somaDasParcelas(nota.parcelas);
  if (compararDinheiro(devido, somado) === 0) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['parcelas'],
    message: `As parcelas somam ${somado} e a nota vale ${devido}. Faltam ${subtrairDinheiro(devido, somado)}.`,
  });
}

/**
 * Valor de frete só existe no `CIF`.
 *
 * `superRefine` sobre o **documento**, e não `refine` no campo do item, porque a
 * recusa é sobre o par modalidade × valor — e os dois moram em níveis
 * diferentes: a modalidade no cabeçalho, o valor em cada linha. É também por isso
 * que a regra não pode ser um `CHECK` do banco, como era enquanto o valor ficava
 * no cabeçalho: nenhum `CHECK` alcança a tabela do pai a partir do filho.
 *
 * A mensagem chega presa ao campo da **linha**, e não ao documento: é ela que o
 * conferente limpa. A outra saída — trocar a modalidade para CIF — está escrita
 * na frase, porque quem digitou o valor provavelmente errou foi a modalidade.
 */
function validarFreteDosItens(
  nota: { frete: FreteNota; itens: readonly { valorFrete: string }[] },
  ctx: z.RefinementCtx,
): void {
  if (freteTemValor(nota.frete)) return;

  nota.itens.forEach((item, indice) => {
    if (Number(item.valorFrete) === 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['itens', indice, 'valorFrete'],
      message:
        'Só o frete CIF tem valor a informar: nele o fornecedor entrega e cobra. Zere o valor, ou mude a modalidade da nota para CIF.',
    });
  });
}

/**
 * Imposto cobrado por dentro não passa do que a linha custa.
 *
 * A recusa que faltava, e o defeito era silencioso: uma nota de R$ 1.000,00 de
 * mercadoria com ICMS destacado de R$ 2.000,00 era aceita, o custo de estoque
 * dava negativo e o piso de `valoresDoItemDeNota` gravava R$ 0,00. Dez unidades a
 * R$ 100,00 no depósito viravam vinte a R$ 50,00 na confirmação, e todo consumo
 * de obra dali em diante saía por metade — com nada na tela acusando, porque zero
 * é um número plausível.
 *
 * A mensagem chega presa ao **campo do imposto da linha**, e não ao documento: é
 * ele que o conferente corrige, e é quase sempre um dígito a mais ou a alíquota
 * aplicada sobre a base errada.
 *
 * Fora do CIF o frete da linha não conta, como em `validarSomaDasParcelas` — o
 * payload o zera, e somá-lo aqui daria um teto maior do que o que se vai gravar.
 */
function validarImpostoPorDentroDosItens(
  nota: { frete: FreteNota; itens: readonly ItemNotaCompraEntrada[] },
  ctx: z.RefinementCtx,
): void {
  const comFrete = freteTemValor(nota.frete);

  nota.itens.forEach((item, indice) => {
    const excesso = excessoDeImpostoPorDentro({
      ...item,
      valorFrete: comFrete ? item.valorFrete : '0',
    });
    if (excesso === null) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['itens', indice, 'impostos', 'valorIcms'],
      message: `O ICMS destacado (${excesso.valorPorDentro}) passa do que esta linha custa (${excesso.valorAPagar}). O ICMS é cobrado por dentro: já está embutido no valor da mercadoria, e não pode ser maior que ela. Confira o valor e a alíquota no documento do fornecedor.`,
    });
  });
}

/**
 * O payload, com as regras do frete, do imposto e do financeiro aplicadas.
 *
 * A base fica privada e só este nome sai do módulo: dois schemas exportados — um
 * com as regras e outro sem — seriam um convite a importar o errado, e o caminho
 * que escapasse da validação seria justamente o de gravação.
 *
 * O frete é validado **antes** das outras duas: com o valor num frete que não o
 * admite, o total esperado seria outro, e o usuário leria duas mensagens sobre o
 * mesmo engano. O imposto vem antes das parcelas pelo mesmo motivo — um ICMS
 * impossível quase sempre veio de um dígito a mais, e o total da nota que as
 * parcelas conferem já estaria errado por causa dele.
 */
export const criarNotaCompraSchema = notaCompraBaseSchema
  .superRefine(validarFreteDosItens)
  .superRefine(validarImpostoPorDentroDosItens)
  .superRefine(validarSomaDasParcelas);

export type CriarNotaCompra = z.infer<typeof notaCompraBaseSchema>;
export const atualizarNotaCompraSchema = criarNotaCompraSchema;
export type AtualizarNotaCompra = z.infer<typeof atualizarNotaCompraSchema>;

/**
 * Nota como a API a devolve.
 *
 * Os nomes de empresa, fornecedor, condição e operação vêm **desnormalizados**: a
 * listagem mostra "Casa do Construtor · 30/60/90" e não quatro uuid, e resolver
 * isso no cliente significaria quatro consultas por linha.
 */
export const notaCompraSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  /** Regime da empresa — é o que explica por que o crédito ficou zerado. */
  empresaRegimeTributario: regimeTributarioSchema.nullable(),
  fornecedorId: uuidSchema,
  /** Nome de uso: fantasia quando há, razão social quando não (`nomeDeUso`). */
  fornecedorNome: z.string().nullable(),
  /** Número e série impressos no documento do fornecedor. */
  numeroNota: z.string(),
  serie: z.string(),
  /** Nula na nota que não gera financeiro. Ver a entrada, e a decisão 24. */
  condicaoPagamentoId: uuidSchema.nullable(),
  /** `descricao` no cadastro — o nome do campo acompanha a tabela de origem. */
  condicaoPagamentoDescricao: z.string().nullable(),
  operacaoFiscalId: uuidSchema,
  operacaoFiscalNome: z.string().nullable(),
  chaveNfe: z.string().nullable(),
  dataEmissao: dataSchema,
  dataLancamento: dataSchema,
  /** A modalidade. O valor cobrado está em cada item — ver `itemNotaCompraSchema`. */
  frete: freteNotaSchema,
  situacao: situacaoNotaSchema,
  /** Marca o congelamento dos créditos. Nulo enquanto pendente. */
  confirmadaEm: dataHoraSchema.nullable(),
  observacao: z.string().nullable(),
  /**
   * Somados na leitura, e não gravados no cabeçalho.
   *
   * `.spec/016`, seção 8, não define coluna de total em `notas_compra`, e a
   * ausência é coerente: cada parcela do número já está gravada nos itens, que
   * são fatos daquela data (ADR 0004). Somá-los é adição, não fórmula sujeita a
   * revisão — o risco que a ADR 0004 evita é o de a *fórmula* mudar, e a de somar
   * não muda.
   */
  valorMercadoria: dinheiroSchema,
  valorAPagar: dinheiroSchema,
  valorCustoEstoque: dinheiroSchema,
  /**
   * Os itens vêm **na listagem também**, não só no documento aberto.
   *
   * É o que distingue duas notas do mesmo fornecedor no mesmo dia, e é o que a
   * coluna "Itens" resume. O custo é uma segunda consulta por página, não uma por
   * linha — a mesma escolha do pedido.
   */
  itens: z.array(itemNotaCompraSchema),
  parcelas: z.array(parcelaDaNotaSchema),
  /** As parcelas vêm do pedido de compra. Ver a entrada. */
  parcelasDoPedido: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type NotaCompra = z.infer<typeof notaCompraSchema>;

export const listaNotasCompraSchema = respostaListaSchema(notaCompraSchema);
export type ListaNotasCompra = z.infer<typeof listaNotasCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: documento não se inativa, e a coluna equivalente é `situacao`. O
 * recorte natural é **situação, fornecedor e período**, e "o que está pendente de
 * confirmação" é a pergunta que a tela abre respondendo.
 *
 * O período recorta por **data de lançamento**, e não por emissão: quem procura
 * uma nota procura pelo dia em que ela foi digitada. A emissão importa para a
 * apuração de créditos, que é outro relatório, e um filtro só que servisse aos
 * dois casos serviria mal aos dois.
 */
export const listarNotasCompraQuerySchema = paginacaoSchema.omit({ q: true }).extend({
  empresaId: uuidSchema.optional(),
  fornecedorId: uuidSchema.optional(),
  situacao: situacaoNotaSchema.optional(),
  operacaoFiscalId: uuidSchema.optional(),
  /**
   * O material comprado, por id.
   *
   * Alcança os **itens**, e não o cabeçalho: a nota não tem insumo, cada linha
   * dela tem. É `EXISTS` no servidor, não junção — a junção traria a nota
   * repetida uma vez por item e a paginação passaria a contar itens.
   */
  insumoId: uuidSchema.optional(),
  /** Recorta pelos rateios dos itens: "o que se comprou para a Aurora". */
  obraId: uuidSchema.optional(),
  /**
   * Recorta pelos itens: traz a nota que atende este pedido.
   *
   * É a pergunta que a tela do pedido faz para explicar por que ele não se
   * altera mais — e a que responde "esta entrega já foi lançada?".
   */
  pedidoCompraId: uuidSchema.optional(),
  /** A chave inteira, para achar a nota que se tem na mão. */
  chaveNfe: z.string().trim().min(1).max(44).optional(),
  /**
   * O número do documento, exato.
   *
   * Ao lado da chave e pelo mesmo motivo — achar a nota que se tem na mão —, e
   * é o caminho que funciona quando o fornecedor não emite NF-e. Exato, e não
   * "contém": "12" traria toda nota que tem 12 em qualquer posição do número, e
   * quem procura pela nota 12 não quer as 1.200 outras.
   */
  numeroNota: z.string().trim().min(1).max(20).optional(),
  /** Início do período de lançamento, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período de lançamento, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarNotasCompraQuery = z.infer<typeof listarNotasCompraQuerySchema>;

export const COLUNAS_ORDENAVEIS_NOTA_COMPRA = [
  'dataLancamento',
  'dataEmissao',
  'situacao',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas entre a tela e a API                                 */
/* -------------------------------------------------------------------------- */

/**
 * A nota ainda se altera?
 *
 * Vive no contrato, e não no formulário nem no service, porque os dois precisam
 * da **mesma** regra: a tela decide se os campos são editáveis, e a API decide se
 * aceita o `PUT`. Duas cópias divergiriam na primeira correção, e a divergência
 * apareceria como formulário que deixa salvar e servidor que recusa.
 *
 * A nota confirmada não se altera **nem se reabre**: alterá-la exigiria desfazer
 * movimentação de estoque, custo médio e títulos, que é justamente o que a
 * exclusão faz, com as verificações que ela tem (`.spec/010`, "Exclusão de Nota de
 * Compra confirmada"). Um "voltar para pendente" seria a mesma reversão sem as
 * verificações.
 */
export function notaEditavel(situacao: SituacaoNotaCompra): boolean {
  return situacao === 'PENDENTE';
}

/* -------------------------------------------------------------------------- */
/* Origem: pedido de compra → nota                                            */
/* -------------------------------------------------------------------------- */

/**
 * Uma fatia de rateio **do pedido**, que é por quantidade e admite obra nula.
 *
 * O tipo é declarado aqui, e não importado de `pedidos-compra.ts`, porque o que
 * a conversão precisa são três campos — e depender do schema inteiro do pedido
 * faria qualquer campo novo lá alcançar esta função.
 */
export interface FatiaDeRateioDoPedido {
  obraId: string | null;
  obraParteId: string | null;
  depositoId: string;
  quantidade: string;
}

/** Cem por cento na escala de quatro casas, em inteiro. */
const CEM_ESCALADO = 100n * 10n ** BigInt(CASAS_PERCENTUAL);

/**
 * Agrupa as fatias por obra × etapa × **depósito**, somando as quantidades.
 *
 * O depósito entrou na chave junto com a mudança do rateio. Antes ele não existia
 * do lado da nota e duas fatias da mesma obra em canteiros diferentes tinham de
 * ser somadas numa só — era o que a nota sabia representar. Agora ela representa
 * as duas, e somá-las voltaria a perder exatamente a informação que esta mudança
 * foi feita para preservar.
 *
 * **A fatia sem obra é agrupada como as outras**, e não descartada aqui: ela pesa
 * na escala, e é `rateioDoPedidoParaNota` quem a deixa de fora no fim. Descartá-la
 * antes de medir faria a parcela da obra crescer para ocupar o lugar dela.
 */
function agruparFatias(fatias: readonly FatiaDeRateioDoPedido[]): {
  obraId: string | null;
  obraParteId: string | null;
  depositoId: string;
  escalada: bigint;
}[] {
  const grupos = new Map<
    string,
    { obraId: string | null; obraParteId: string | null; depositoId: string; escalada: bigint }
  >();

  for (const fatia of fatias) {
    const chave = `${fatia.obraId ?? ''}|${fatia.obraParteId ?? ''}|${fatia.depositoId}`;
    const atual = grupos.get(chave);
    const escalada = escalar(fatia.quantidade, CASAS_QUANTIDADE);

    if (atual) atual.escalada += escalada;
    else
      grupos.set(chave, {
        obraId: fatia.obraId,
        obraParteId: fatia.obraParteId,
        depositoId: fatia.depositoId,
        escalada,
      });
  }

  return [...grupos.values()];
}

/**
 * Converte o rateio de um item de pedido no rateio do item de nota.
 *
 * **É sugestão, e não transcrição.** Quem manda é a nota: o rateio dela é o que
 * vale, o que move estoque e o que o contas a pagar herda. O do pedido entra uma
 * vez, para poupar digitação, e o conferente edita o que quiser. Vive no contrato
 * porque a tela de entrada por pedido a executa e o resultado é gravado pela API
 * — uma segunda implementação faria o rateio exibido divergir do salvo.
 *
 * `quantidadeDaNota` é o que a linha da nota diz ter chegado, e é ele que manda a
 * escala: o pedido pediu cem sacos, a nota trouxe sessenta, e as fatias encolhem
 * na mesma proporção. Usar a quantidade do pedido sugeriria distribuir material
 * que não chegou.
 *
 * **A fatia sem obra some, e só ela.** No pedido, obra nula é a parcela de
 * almoxarifado central; na nota, toda fatia tem obra, e o que não é rateado entra
 * no depósito do item — que descreve a mesma coisa. Então a sugestão leva o que o
 * pedido sabia responder e deixa o resto em aberto, em vez de descartar o rateio
 * inteiro por causa de uma fatia. **Isto reverte a decisão anterior**, que
 * descartava tudo: ela existia porque a soma das fatias tinha de fechar a
 * quantidade da linha, e uma sugestão parcial nasceria inválida. A soma passou a
 * ser um teto, não uma igualdade.
 *
 * A escala é calculada sobre **todas** as fatias, inclusive as sem obra, e só
 * depois elas são descartadas. É o que faz os dez sacos da obra continuarem dez
 * quando a nota traz os quinze — e não quinze, que é o que uma proporção medida
 * só entre as fatias com obra produziria.
 *
 * Fatias da mesma obra, etapa **e depósito** são somadas; em depósitos diferentes
 * continuam separadas — ver `agruparFatias`. A fatia que arredonda para zero é
 * descartada. A sobra do arredondamento vai para as de **maior resto**, com
 * empate pela ordem — a mesma mecânica de `ratear`, em `@construtora/domain`, e o
 * que torna o resultado determinístico: conferir a mesma nota duas vezes dá o
 * mesmo rascunho.
 */
export function rateioDoPedidoParaNota(
  quantidadeDaNota: string,
  fatias: readonly FatiaDeRateioDoPedido[],
): RateioDoItemDeNotaEntrada[] {
  if (fatias.length === 0) return [];

  const grupos = agruparFatias(fatias);
  const total = grupos.reduce((soma, grupo) => soma + grupo.escalada, 0n);
  const alvo = escalar(quantidadeDaNota, CASAS_QUANTIDADE);
  if (total <= 0n || alvo <= 0n) return [];

  const parciais = grupos.map((grupo, posicao) => {
    const bruto = grupo.escalada * alvo;
    return { grupo, posicao, quantidade: bruto / total, resto: bruto % total };
  });

  let sobra = alvo - parciais.reduce((soma, p) => soma + p.quantidade, 0n);

  // Maior resto primeiro; empate pela ordem de cadastro.
  const porResto = [...parciais].sort((a, b) => {
    if (a.resto !== b.resto) return a.resto > b.resto ? -1 : 1;
    return a.posicao - b.posicao;
  });

  for (const parcial of porResto) {
    if (sobra <= 0n) break;
    parcial.quantidade += 1n;
    sobra -= 1n;
  }

  return parciais
    .filter((parcial) => parcial.grupo.obraId !== null && parcial.quantidade > 0n)
    .map((parcial) => ({
      // `as string` sustentado pelo filtro logo acima.
      obraId: parcial.grupo.obraId as string,
      obraParteId: parcial.grupo.obraParteId,
      depositoId: parcial.grupo.depositoId,
      quantidade: desescalar(parcial.quantidade, CASAS_QUANTIDADE),
    }));
}

/**
 * Resumo dos itens, para a listagem: `3 itens · 2 no estoque`.
 *
 * Vive no contrato pelo mesmo motivo que `resumoDeItensDoPedido`: a listagem e
 * qualquer futura tela que aponte para a nota mostram a mesma coisa.
 *
 * O segundo número conta os itens cuja operação **movimenta estoque**, e não os
 * que geram financeiro: é a pergunta que se faz ao conferir por que uma nota
 * confirmada não apareceu no depósito.
 */
export function resumoDeItensDaNota(
  itens: readonly { operacaoAtualizaEstoque: boolean }[],
): string {
  if (itens.length === 0) return '—';

  const plural = itens.length === 1 ? 'item' : 'itens';
  const noEstoque = itens.filter((item) => item.operacaoAtualizaEstoque).length;

  if (noEstoque === 0) return `${itens.length} ${plural} · nenhum no estoque`;
  if (noEstoque === itens.length) return `${itens.length} ${plural} · todos no estoque`;
  return `${itens.length} ${plural} · ${noEstoque} no estoque`;
}

/* -------------------------------------------------------------------------- */
/* Confirmação: o que a nota escreve nos títulos                              */
/* -------------------------------------------------------------------------- */

/**
 * A nota ainda pode ser confirmada?
 *
 * Hoje coincide com `notaEditavel`, e as duas existem separadas de propósito: a
 * edição e a confirmação respondem a perguntas diferentes, e no dia em que
 * aparecer uma terceira situação — "em conferência", que se altera e não se
 * confirma — a coincidência acaba. Uma função só obrigaria a descobrir isso
 * pelos dois lugares que a chamam.
 */
export function notaConfirmavel(situacao: SituacaoNotaCompra): boolean {
  return situacao === 'PENDENTE';
}

/**
 * O que se deve por **uma linha**: mercadoria líquida mais IPI e ICMS ST.
 *
 * É o peso de cada item na divisão do título — e não o custo de estoque, que já
 * desconta os créditos. Crédito reduz o custo do material, não o que se paga, e
 * usá-lo aqui faria a linha com crédito integral pesar menos no rateio do que
 * ela pesa na dívida.
 *
 * O **frete entra**, e a mudança é consequência de ele ter descido para a linha:
 * ele já está dentro de `valorTotal`. Antes, sendo um valor único do cabeçalho,
 * ele ficava de fora e acabava distribuído entre obras e contas contábeis na
 * proporção das mercadorias — a única repartição que os dados então sustentavam.
 * Agora o transporte pesa na obra que de fato o consumiu, porque quem disse a
 * qual linha ele pertence foi quem lançou a nota.
 */
export function valorAPagarDoItem(item: {
  valorTotal: string;
  impostos: { valorIpi: string; valorIcmsSt: string };
}): string {
  return somarDinheiro([item.valorTotal, item.impostos.valorIpi, item.impostos.valorIcmsSt]);
}

/** Um grupo de rateio já ponderado, antes de virar percentual. */
interface GrupoPonderado<T> {
  readonly dados: T;
  readonly peso: bigint;
}

/**
 * Converte pesos em índices percentuais de 4 casas.
 *
 * A soma dos índices é **a fração que os grupos representam do total**, e não
 * necessariamente 100%: com todos os itens cobertos, a divisão é exata e dá
 * 100,0000; com parte do valor fora dos grupos — a compra de almoxarifado
 * central, que não se rateia por obra — dá o percentual que de fato corresponde.
 * Escalar até fechar 100% atribuiria às obras um custo que não era delas.
 *
 * A sobra do arredondamento vai para os grupos de **maior resto**, com empate
 * pela ordem de entrada, como em `ratear` de `@construtora/domain` e em
 * `rateioDoPedidoParaNota` aqui. O resultado é determinístico, que é o que
 * permite conferir a mesma nota duas vezes e obter o mesmo rateio.
 *
 * Grupo que arredonda para zero é **descartado**: o `CHECK` do banco recusa
 * índice zero, e uma fatia de menos de 0,0001% do título não se representa em
 * quatro casas. Descartá-lo não altera a soma — ele contribuía com zero —, e o
 * peso dele já foi absorvido pelos demais no arredondamento.
 */
function indicesPorPeso<T>(
  grupos: readonly GrupoPonderado<T>[],
  totalPonderado: bigint,
): { dados: T; indiceRateio: string }[] {
  if (grupos.length === 0 || totalPonderado <= 0n) return [];

  const parciais = grupos.map((grupo, posicao) => {
    const bruto = grupo.peso * CEM_ESCALADO;
    return { grupo, posicao, indice: bruto / totalPonderado, resto: bruto % totalPonderado };
  });

  // Quanto o conjunto vale, em índice, arredondado ao 0,0001% mais próximo. É o
  // alvo que a distribuição da sobra tem de alcançar — e é `CEM_ESCALADO` quando
  // os grupos cobrem o total inteiro.
  const somaDosPesos = grupos.reduce((soma, grupo) => soma + grupo.peso, 0n);
  const alvo = (somaDosPesos * CEM_ESCALADO + totalPonderado / 2n) / totalPonderado;

  let sobra = alvo - parciais.reduce((soma, parcial) => soma + parcial.indice, 0n);

  const porResto = [...parciais].sort((a, b) => {
    if (a.resto !== b.resto) return a.resto > b.resto ? -1 : 1;
    return a.posicao - b.posicao;
  });

  for (const parcial of porResto) {
    if (sobra <= 0n) break;
    parcial.indice += 1n;
    sobra -= 1n;
  }

  return parciais
    .filter((parcial) => parcial.indice > 0n)
    .map((parcial) => ({
      dados: parcial.grupo.dados,
      indiceRateio: desescalar(parcial.indice, CASAS_PERCENTUAL),
    }));
}

/** O peso de cada item na divisão do título, em centavos escalados. */
function pesosDosItens(
  itens: readonly { valorTotal: string; impostos: { valorIpi: string; valorIcmsSt: string } }[],
): bigint[] {
  return itens.map((item) => escalar(valorAPagarDoItem(item), CASAS_DINHEIRO));
}

/**
 * A classificação contábil do título, a partir das contas dos itens
 * (`contas_pagar_contas_contabeis`).
 *
 * O índice de cada conta é o **somatório do valor dos itens dela**, em
 * percentual do título. É o que substitui a coluna única de conta contábil que a
 * `.spec/016`, seção 10, previa: uma parcela cobre a nota inteira, e a nota
 * mistura naturezas contábeis.
 *
 * Item sem conta contábil entra no total e em grupo nenhum — o resultado somaria
 * menos de 100%, que é a leitura honesta de "parte deste título não está
 * classificada". Não acontece na prática: o service recusa a gravação da nota
 * que gera financeiro com item sem conta, para o erro chegar com o formulário
 * aberto em vez de na confirmação.
 */
export function rateioContabilDoTitulo(
  itens: readonly {
    contaContabilId: string | null;
    valorTotal: string;
    impostos: { valorIpi: string; valorIcmsSt: string };
  }[],
): { contaContabilId: string; indiceRateio: string }[] {
  const pesos = pesosDosItens(itens);
  const total = pesos.reduce((soma, peso) => soma + peso, 0n);

  const porConta = new Map<string, bigint>();
  itens.forEach((item, indice) => {
    const conta = item.contaContabilId;
    if (conta === null) return;

    porConta.set(conta, (porConta.get(conta) ?? 0n) + (pesos[indice] as bigint));
  });

  const grupos = [...porConta].map(([contaContabilId, peso]) => ({
    dados: { contaContabilId },
    peso,
  }));

  return indicesPorPeso(grupos, total).map(({ dados, indiceRateio }) => ({
    contaContabilId: dados.contaContabilId,
    indiceRateio,
  }));
}

/**
 * O rateio de obra do título, a partir dos rateios dos itens (`.spec/010`:
 * "usar o rateio de obra para gerar o rateio do contas a pagar").
 *
 * A ponderação é pelo **valor** de cada item, e não pelo número de fatias: numa
 * nota em que a linha de R$ 9.000,00 é toda da Aurora e a de R$ 1.000,00 toda da
 * Bela Vista, o título se divide em 90% e 10%. Somar os índices das linhas daria
 * 50% e 50%, que é a média de dois percentuais — uma grandeza que não descreve
 * dinheiro nenhum.
 *
 * Fatias da mesma obra e etapa vindas de itens diferentes são **somadas**: o
 * índice único do rateio do título as recusaria separadas, e duas linhas para a
 * mesma obra são um lançamento que ninguém concilia depois.
 *
 * A soma pode ser **menor que 100%**, e é o caso comum: o item sem rateio é a
 * compra de almoxarifado central, e o consumo dirá depois para qual obra o
 * material foi. Lista vazia significa que nenhuma linha foi rateada.
 */
export function rateioDeObraDoTitulo(
  itens: readonly {
    valorTotal: string;
    impostos: { valorIpi: string; valorIcmsSt: string };
    rateios: readonly { obraId: string; obraParteId: string | null; indiceRateio: string }[];
  }[],
): { obraId: string; obraParteId: string | null; indiceRateio: string }[] {
  const pesos = pesosDosItens(itens);
  // O total é ponderado na mesma escala dos grupos — valor × índice —, e por
  // isso cada item entra multiplicado por 100%. Sem isso, o item sem rateio
  // sairia da conta e as obras absorveriam o custo do almoxarifado.
  const total = pesos.reduce((soma, peso) => soma + peso, 0n) * CEM_ESCALADO;

  const porObra = new Map<string, { obraId: string; obraParteId: string | null; peso: bigint }>();

  itens.forEach((item, indice) => {
    const valor = pesos[indice] as bigint;

    for (const rateio of item.rateios) {
      const chave = `${rateio.obraId}|${rateio.obraParteId ?? ''}`;
      const peso = valor * escalar(rateio.indiceRateio, CASAS_PERCENTUAL);
      const atual = porObra.get(chave);

      if (atual) atual.peso += peso;
      else porObra.set(chave, { obraId: rateio.obraId, obraParteId: rateio.obraParteId, peso });
    }
  });

  const grupos = [...porObra.values()].map((grupo) => ({
    dados: { obraId: grupo.obraId, obraParteId: grupo.obraParteId },
    peso: grupo.peso,
  }));

  return indicesPorPeso(grupos, total).map(({ dados, indiceRateio }) => ({
    obraId: dados.obraId,
    obraParteId: dados.obraParteId,
    indiceRateio,
  }));
}

/*
  `quantidadePorFatiaDeRateio` saiu daqui.

  Ela repartia a quantidade do item pelos índices de rateio, e existia porque a
  fatia não tinha quantidade própria: o consumo automático precisava de uma, e
  derivá-la do percentual de dinheiro era a única informação que a nota trazia.
  Agora a fatia traz a quantidade dela, e derivar de volta seria reconstruir por
  conta o número que está gravado ao lado.
*/
